// Table.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress"; // Asegúrate de importar CircularProgress

const formatearMes = (mes) => {
  const meses = {
    enero: "01",
    febrero: "02",
    marzo: "03",
    abril: "04",
    mayo: "05",
    junio: "06",
    julio: "07",
    agosto: "08",
    septiembre: "09",
    octubre: "10",
    noviembre: "11",
    diciembre: "12",
  };

  if (!mes) return "N/A";
  if (meses[mes.toLowerCase()]) return meses[mes.toLowerCase()];
  if (/^\d{2}$/.test(mes)) return mes;
  return "N/A";
};

const formatearFecha = (dia, mes, ano) => {
  const mesFormateado = formatearMes(mes);
  const diaFormateado = dia.toString().padStart(2, "0");
  const anoFormateado = ano || "N/A";

  return `${diaFormateado}-${mesFormateado}-${anoFormateado}`;
};

const esFechaInvalida = (dia, mes, ano) => {
  const mesFormateado = formatearMes(mes);
  return dia === "N/A" || mesFormateado === "N/A" || ano === "N/A";
};

const calcularHorasTrabajadas = (horaEntrada, horaSalida) => {
  if (
    horaEntrada === "AUSENTE" ||
    horaSalida === "AUSENTE" ||
    horaEntrada === "N/A" ||
    horaSalida === "N/A"
  ) {
    return "0:00";
  }

  const [horaE, minutoE] = horaEntrada.split(":").map(Number);
  const [horaS, minutoS] = horaSalida.split(":").map(Number);

  if (isNaN(horaE) || isNaN(minutoE) || isNaN(horaS) || isNaN(minutoS)) {
    return "NaN:NaN";
  }

  let horasTrabajadas = horaS - horaE;
  let minutosTrabajados = minutoS - minutoE;

  if (minutosTrabajados < 0) {
    minutosTrabajados += 60;
    horasTrabajadas -= 1;
  }

  if (horasTrabajadas < 0) {
    horasTrabajadas += 24;
  }

  return `${horasTrabajadas}:${minutosTrabajados.toString().padStart(2, "0")}`;
};

const esHorasTrabajadasFueraRango = (horasTrabajadas) => {
  const [horas, minutos] = horasTrabajadas.split(":").map(Number);
  return (
    horas > 10 ||
    (horas < 4 && horas !== 0) ||
    horasTrabajadas === "NaN:NaN"
  );
};

const validarFechaFormato = (fecha) => {
  // Ejemplo de validación de formato DD-MM-AAAA
  const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
  return regex.test(fecha);
};

const Table = ({ resultados, onOpenModal, reintentarEnvio, onUpdateField }) => {
  const {
    archivoId,
    archivoName,
    estado,
    NOMBRE,
    CODIGO,
    RUT,
    MES,
    ANO,
    Asistencia,
    OBSERVACIONES,
    mensajeError,
  } = resultados;

  const [editValues, setEditValues] = useState({});
  const [isEditing, setIsEditing] = useState({});
  const [data, setData] = useState(resultados);

  useEffect(() => {
    setData(resultados);
  }, [resultados]);

  const toggleEdit = (asistenciaIndex) => {
    setIsEditing((prev) => ({
      ...prev,
      [asistenciaIndex]: !prev[asistenciaIndex],
    }));

    if (!isEditing[asistenciaIndex]) {
      const asistencia = data.Asistencia[asistenciaIndex];
      setEditValues((prev) => ({
        ...prev,
        [asistenciaIndex]: {
          NOMBRE: NOMBRE || "",
          RUT: RUT || "",
          CODIGO: CODIGO || "",
          fecha: formatearFecha(asistencia.DIA, MES, ANO),
          OBSERVACIONES: OBSERVACIONES || "",
          HORA_ENTRADA: asistencia.HORA_ENTRADA || "",
          HORA_SALIDA: asistencia.HORA_SALIDA || "",
        },
      }));
    } else {
      // Limpiar los valores de edición al salir del modo de edición
      setEditValues((prev) => {
        const newEditValues = { ...prev };
        delete newEditValues[asistenciaIndex];
        return newEditValues;
      });
    }
  };

  const handleSave = (asistenciaIndex) => {
    const updatedValues = editValues[asistenciaIndex] || {};
    const asistencia = data.Asistencia[asistenciaIndex];

    // Recalcular las horas trabajadas
    const horasTrabajadas = calcularHorasTrabajadas(
      updatedValues.HORA_ENTRADA || asistencia.HORA_ENTRADA,
      updatedValues.HORA_SALIDA || asistencia.HORA_SALIDA
    );

    // Validaciones
    const esInvalida =
      esFechaInvalida(asistencia.DIA, MES, ANO) ||
      esHoraInvalida(updatedValues.HORA_ENTRADA, updatedValues.HORA_SALIDA) ||
      esHorasTrabajadasFueraRango(horasTrabajadas) ||
      !validarFechaFormato(updatedValues.fecha);

    // Parsear la fecha editada
    let [dia, mes, ano] = updatedValues.fecha
      ? updatedValues.fecha.split("-")
      : [asistencia.DIA, MES, ANO];

    const diaParsed = dia && dia !== "N/A" ? dia : "N/A";
    const mesParsed = mes && mes !== "N/A" ? formatearMes(mes) : "N/A";
    const anoParsed = ano && ano !== "N/A" ? ano : "N/A";

    // Crear el objeto de asistencia actualizado
    const updatedAsistencia = {
      ...asistencia,
      DIA: diaParsed,
      HORA_ENTRADA: updatedValues.HORA_ENTRADA || asistencia.HORA_ENTRADA,
      HORA_SALIDA: updatedValues.HORA_SALIDA || asistencia.HORA_SALIDA,
      horasTrabajadas,
      esInvalida: esInvalida,
    };

    // Actualizar el nombre y observaciones en el resultado principal
    const updatedNombre = updatedValues.NOMBRE || NOMBRE || "";
    const updatedCodigo = updatedValues.CODIGO || CODIGO || "";
    const updatedRUT = updatedValues.RUT || RUT || "";
    const updatedObservaciones = updatedValues.OBSERVACIONES || OBSERVACIONES || "";

    // Llamar a la función para actualizar el estado en `index.js`
    onUpdateField(archivoId, asistenciaIndex, updatedAsistencia, {
      NOMBRE: updatedNombre,
      OBSERVACIONES: updatedObservaciones,
      RUT: updatedRUT,
      CODIGO: updatedCodigo,
      DIA: diaParsed,
      MES: mesParsed,
      ANO: anoParsed,
    });

    // Salir del modo de edición
    toggleEdit(asistenciaIndex);
  };

  const handleInputChange = (asistenciaIndex, field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [asistenciaIndex]: {
        ...prev[asistenciaIndex],
        [field]: value,
      },
    }));
  };

  if (!data) {
    return <div>No hay datos disponibles</div>;
  }

  const esHoraInvalida = (horaEntrada, horaSalida) => {
    return horaEntrada === "N/A" || horaSalida === "N/A";
  };

  const mostrarBotones = estado !== "procesando";

  return (
    <div className="tabla-responsiva">
 <table>
        <thead>
          <tr>
            <th>Nombre Archivo</th>
            <th>Estado</th>
            <th>Nombre</th>
            <th>RUT</th>
            <th>Código</th>
            <th>Fecha</th>
            <th>Hora Entrada</th>
            <th>Hora Salida</th>
            <th>Horas Trabajadas</th>
            <th>Observaciones</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {Asistencia.length === 0 ? (
            <tr>
              <td colSpan="11">
                {estado === "error" ? (
                  <>
                    Error al procesar {archivoName}: {mensajeError}
                  </>
                ) : estado === "reintentando" ? (
                  <>
                    Reintentando {archivoName}...
                    <CircularProgress size={24} />
                  </>
                ) : (
                  <>
                    Procesando {archivoName}...
                    <CircularProgress size={24} />
                  </>
                )}
              </td>
            </tr>
          ) : (
            Asistencia.map((asistencia, index) => (
              <tr key={`${archivoId}-${index}`}>
                <td>{archivoName}</td>
                <td>{estado}</td>
                <td>{NOMBRE}</td>
                <td>{RUT}</td>
                <td>{CODIGO}</td>
                <td>{formatearFecha(asistencia.DIA, MES, ANO)}</td>
                <td>
                  {isEditing[index] ? (
                    <input
                      value={editValues[index]?.HORA_ENTRADA || asistencia.HORA_ENTRADA}
                      onChange={(e) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [index]: { ...prev[index], HORA_ENTRADA: e.target.value },
                        }))
                      }
                    />
                  ) : (
                    asistencia.HORA_ENTRADA
                  )}
                </td>
                <td>
                  {isEditing[index] ? (
                    <input
                      value={editValues[index]?.HORA_SALIDA || asistencia.HORA_SALIDA}
                      onChange={(e) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [index]: { ...prev[index], HORA_SALIDA: e.target.value },
                        }))
                      }
                    />
                  ) : (
                    asistencia.HORA_SALIDA
                  )}
                </td>
                <td>{calcularHorasTrabajadas(asistencia.HORA_ENTRADA, asistencia.HORA_SALIDA)}</td>
                <td>
                  {isEditing[index] ? (
                    <textarea
                      value={editValues[index]?.OBSERVACIONES || OBSERVACIONES}
                      onChange={(e) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [index]: { ...prev[index], OBSERVACIONES: e.target.value },
                        }))
                      }
                    />
                  ) : (
                    OBSERVACIONES
                  )}
                </td>
                <td>
                  {!isEditing[index] ? (
                    <button onClick={() => toggleEdit(index)}>Editar</button>
                  ) : (
                    <button onClick={() => handleSave(index)}>Guardar</button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

    </div>
  );
};

Table.propTypes = {
  resultados: PropTypes.shape({
    archivoName: PropTypes.string.isRequired,
    estado: PropTypes.string.isRequired,
    NOMBRE: PropTypes.string,
    RUT: PropTypes.string, // Agregar RUT
    CODIGO: PropTypes.string, // Agregar Código
    MES: PropTypes.string,
    ANO: PropTypes.string,
    Asistencia: PropTypes.arrayOf(
      PropTypes.shape({
        DIA: PropTypes.string.isRequired,
        HORA_ENTRADA: PropTypes.string.isRequired,
        HORA_SALIDA: PropTypes.string.isRequired,
      })
    ).isRequired,
    archivoId: PropTypes.string.isRequired,
    OBSERVACIONES: PropTypes.string,
    mensajeError: PropTypes.string, // Asegúrate de incluir este campo si se usa
  }).isRequired,
  reintentarEnvio: PropTypes.func.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  onUpdateField: PropTypes.func.isRequired,
};


export default Table;
